import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, LogOut, Download, Search, Users, Mail, Phone, CalendarDays, Inbox, BarChart3, Reply, CheckCircle2, Undo2, Trash2, StickyNote, BellRing, Quote, Star, Plus,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import Logo from "@/components/Logo";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "h-12 rounded-xl border-[#C9DBDF] bg-[#F4F9FA] text-[#1A2E33] placeholder:text-[#9AB0B7] focus-visible:ring-[#1F8296]/50";
const cardCls = "rounded-3xl border border-[#D8E5E8] bg-white shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)]";

const FILTERS = [
  { key: "aperti", label: "Da lavorare" },
  { key: "lavorati", label: "Lavorati" },
  { key: "tutti", label: "Tutti" },
];

const downloadBlob = (data, filename) => {
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const EMPTY_TESTI = { nome: "", corso: "", testo: "", rating: 5 };

export default function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = logged out
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("aperti");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [stats, setStats] = useState(null);

  const [testis, setTestis] = useState([]);
  const [tForm, setTForm] = useState(EMPTY_TESTI);
  const [tSaving, setTSaving] = useState(false);

  const loadLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const { data } = await api.get("/admin/contacts");
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLeadsLoading(false);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  const loadTestis = useCallback(async () => {
    try {
      const { data } = await api.get("/testimonials");
      setTestis(data);
    } catch {
      setTestis([]);
    }
  }, []);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setAuth(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setAuth(data);
        } catch {
          setAuth(false);
        }
      }
    };
    check();
  }, []);

  useEffect(() => {
    if (auth) {
      loadLeads();
      loadStats();
      loadTestis();
    }
  }, [auth, loadLeads, loadStats, loadTestis]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAuth(data);
      toast.success(`Bentornato, ${data.name || "Admin"}`);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoginLoading(false);
    }
  };

  const onLogout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setAuth(false);
      setLeads([]);
      setStats(null);
      setTestis([]);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/contacts/export", { responseType: "blob" });
      downloadBlob(res.data, "contatti_scuola_sicura.csv");
      toast.success("CSV esportato con successo");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/admin/contacts/export-xlsx", { responseType: "blob" });
      downloadBlob(res.data, "contatti_scuola_sicura.xlsx");
      toast.success("File Excel esportato con successo");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportAnalytics = async () => {
    try {
      const res = await api.get("/admin/analytics/export", { responseType: "blob" });
      downloadBlob(res.data, "visite_scuola_sicura.csv");
      toast.success("CSV visite esportato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openLead = (lead) => {
    setSelected(lead);
    setNoteDraft(lead.note || "");
    setConfirmDelete(false);
  };

  const toggleLavorato = async (lead) => {
    const next = !lead.lavorato;
    try {
      await api.patch(`/admin/contacts/${lead.id}`, { lavorato: next });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, lavorato: next } : l)));
      setSelected((s) => (s && s.id === lead.id ? { ...s, lavorato: next } : s));
      toast.success(next ? `${lead.nome} segnato come lavorato` : `${lead.nome} rimesso tra i da lavorare`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveNote = async () => {
    if (!selected) return;
    setNoteSaving(true);
    try {
      await api.patch(`/admin/contacts/${selected.id}`, { note: noteDraft });
      setLeads((ls) => ls.map((l) => (l.id === selected.id ? { ...l, note: noteDraft } : l)));
      setSelected((s) => (s ? { ...s, note: noteDraft } : s));
      toast.success("Nota salvata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteLead = async () => {
    if (!selected) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    try {
      await api.delete(`/admin/contacts/${selected.id}`);
      setLeads((ls) => ls.filter((l) => l.id !== selected.id));
      toast.success(`Contatto di ${selected.nome} eliminato definitivamente`);
      setSelected(null);
      setConfirmDelete(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addTesti = async (e) => {
    e.preventDefault();
    setTSaving(true);
    try {
      await api.post("/admin/testimonials", tForm);
      toast.success("Testimonianza pubblicata sul sito");
      setTForm(EMPTY_TESTI);
      loadTestis();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTSaving(false);
    }
  };

  const deleteTesti = async (t) => {
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      setTestis((ls) => ls.filter((x) => x.id !== t.id));
      toast.success(`Testimonianza di ${t.nome} eliminata`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const counts = useMemo(() => ({
    aperti: leads.filter((l) => !l.lavorato).length,
    lavorati: leads.filter((l) => l.lavorato).length,
    tutti: leads.length,
  }), [leads]);

  const stale = useMemo(() => {
    const cutoff = Date.now() - 3 * 24 * 60 * 60 * 1000;
    return leads.filter((l) => !l.lavorato && new Date(l.created_at).getTime() < cutoff);
  }, [leads]);

  const filtered = useMemo(() => {
    let list = leads;
    if (filter === "aperti") list = list.filter((l) => !l.lavorato);
    if (filter === "lavorati") list = list.filter((l) => l.lavorato);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((l) =>
        [l.nome, l.email, l.telefono, l.oggetto, l.messaggio]
          .filter(Boolean)
          .some((v) => v.toLowerCase().includes(q))
      );
    }
    return list;
  }, [leads, query, filter]);

  const chartData = useMemo(() => {
    if (!stats) return [];
    const map = Object.fromEntries(stats.days.map((d) => [d.date, d.count]));
    const out = [];
    for (let i = 6; i >= 0; i--) {
      const dt = new Date();
      dt.setDate(dt.getDate() - i);
      const key = dt.toISOString().slice(0, 10);
      out.push({
        label: dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
        visite: map[key] || 0,
      });
    }
    return out;
  }, [stats]);

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  if (auth === null) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#F2F7F8]">
        <Loader2 className="h-8 w-8 animate-spin text-[#1F8296]" data-testid="admin-loading" />
      </main>
    );
  }

  if (auth === false) {
    return (
      <main className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#F2F7F8] px-5">
        <div className="bg-grid-pattern pointer-events-none absolute inset-0 opacity-60" />
        <div className="animate-float-slow pointer-events-none absolute -top-24 right-[-10%] h-[380px] w-[380px] rounded-full bg-[#2997AB]/10 blur-3xl" />
        <div className={`relative w-full max-w-md p-8 shadow-2xl sm:p-10 ${cardCls}`} data-testid="admin-login-card">
          <Logo compact />
          <h1 className="mt-8 font-display text-2xl font-semibold text-[#1A2E33]">Area Riservata</h1>
          <p className="mt-2 text-sm text-[#5C7178]">
            Accedi per consultare ed esportare i contatti ricevuti dal sito.
          </p>
          <form onSubmit={onLogin} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label htmlFor="admin-email" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Email</label>
              <Input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@scuoladidatticasicura.it"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Password</label>
              <Input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className={inputCls}
              />
            </div>
            {loginError && (
              <p className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loginLoading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2997AB] px-8 py-3.5 text-sm font-bold text-white transition-colors hover:bg-[#238496] disabled:opacity-60"
            >
              {loginLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loginLoading ? "Accesso in corso..." : "Accedi"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F2F7F8] pb-20 pt-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Logo compact />
          <button
            type="button"
            data-testid="admin-logout-button"
            onClick={onLogout}
            className="inline-flex items-center gap-2 rounded-full border border-[#C9DBDF] bg-white px-5 py-2.5 text-sm font-semibold text-[#33484F] transition-colors hover:border-red-400/60 hover:text-red-500"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>

        <div className="mt-10 flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Area riservata</p>
            <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
              Contatti ricevuti
            </h1>
          </div>
          <div className="flex gap-2.5">
            <button
              type="button"
              data-testid="export-csv-button"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
            >
              <Download className="h-4 w-4" /> Esporta CSV
            </button>
            <button
              type="button"
              data-testid="export-xlsx-button"
              onClick={exportXlsx}
              className="inline-flex items-center gap-2 rounded-full border border-[#C9DBDF] bg-white px-6 py-3 text-sm font-bold text-[#1F8296] transition-colors hover:border-[#1F8296]/60"
            >
              <Download className="h-4 w-4" /> Esporta XLS
            </button>
          </div>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <div className={`flex items-center gap-4 p-6 ${cardCls}`}>
            <Users className="h-6 w-6 text-[#1F8296]" />
            <div>
              <p className="font-display text-2xl font-semibold text-[#1A2E33]" data-testid="leads-count">{counts.aperti}</p>
              <p className="text-xs text-[#5C7178]">Contatti da lavorare</p>
            </div>
          </div>
          <div className={`flex items-center gap-4 p-6 ${cardCls}`}>
            <CalendarDays className="h-6 w-6 text-[#1F8296]" />
            <div>
              <p className="font-display text-2xl font-semibold text-[#1A2E33]">
                {leads.length ? formatDate(leads[0].created_at).split(",")[0] : "—"}
              </p>
              <p className="text-xs text-[#5C7178]">Ultimo contatto</p>
            </div>
          </div>
        </div>

        {stale.length > 0 && (
          <div className="mt-5 rounded-3xl border border-orange-300 bg-orange-50 p-6" data-testid="stale-reminder">
            <div className="flex items-center gap-3">
              <BellRing className="h-5 w-5 shrink-0 text-orange-500" />
              <h2 className="font-display text-lg font-semibold text-[#1A2E33]">
                {stale.length === 1
                  ? "1 contatto attende risposta da più di 3 giorni"
                  : `${stale.length} contatti attendono risposta da più di 3 giorni`}
              </h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {stale.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`stale-lead-${l.id}`}
                  onClick={() => openLead(l)}
                  className="rounded-full border border-orange-300 bg-white px-4 py-2 text-xs font-semibold text-orange-600 transition-colors hover:border-orange-500"
                >
                  {l.nome} — {l.oggetto}
                </button>
              ))}
            </div>
          </div>
        )}

        {stats && (
          <div className={`mt-5 p-6 ${cardCls}`} data-testid="analytics-section">
            <div className="flex flex-wrap items-center gap-3">
              <BarChart3 className="h-5 w-5 text-[#1F8296]" />
              <h2 className="font-display text-lg font-semibold text-[#1A2E33]">Visite anonime — ultimi 7 giorni</h2>
              <button
                type="button"
                data-testid="export-analytics-button"
                onClick={exportAnalytics}
                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[#C9DBDF] px-4 py-2 text-xs font-bold text-[#1F8296] transition-colors hover:border-[#1F8296]/60"
              >
                <Download className="h-3.5 w-3.5" /> Esporta CSV
              </button>
              <span className="font-display text-2xl font-semibold text-[#1A2E33]" data-testid="analytics-total">{stats.total}</span>
            </div>

            <div className="mt-6 h-44" data-testid="analytics-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -22, bottom: 0 }}>
                  <defs>
                    <linearGradient id="visiteFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2997AB" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#2997AB" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E3EDEF" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#7A8F96" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#7A8F96" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: 12, border: "1px solid #D8E5E8", fontSize: 12 }}
                    labelStyle={{ color: "#1A2E33", fontWeight: 600 }}
                  />
                  <Area type="monotone" dataKey="visite" stroke="#2997AB" strokeWidth={2.5} fill="url(#visiteFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {stats.top_pages.length > 0 && (
              <div className="mt-6 space-y-3">
                {stats.top_pages.map((p) => (
                  <div key={p.path} className="flex items-center gap-4">
                    <span className="w-32 shrink-0 truncate font-plex text-xs text-[#5C7178]">{p.path}</span>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E4EFF1]">
                      <div
                        className="h-full rounded-full bg-[#2997AB]/70"
                        style={{ width: `${Math.max(6, stats.total ? (p.count / stats.total) * 100 : 0)}%` }}
                      />
                    </div>
                    <span className="w-10 text-right text-xs text-[#5C7178]">{p.count}</span>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs text-[#7A8F96]">
              Raccolte solo dai visitatori che accettano i cookie analitici, in forma aggregata e anonima. Il CSV include gli ultimi 30 giorni.
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="leads-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                filter === f.key
                  ? "bg-[#2997AB] text-white"
                  : "border border-[#C9DBDF] bg-white text-[#5C7178] hover:border-[#1F8296]/50 hover:text-[#1F8296]"
              }`}
            >
              {f.label} ({counts[f.key]})
            </button>
          ))}
        </div>

        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9AB0B7]" />
          <Input
            data-testid="leads-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, email, telefono, oggetto..."
            className={`${inputCls} pl-11`}
          />
        </div>

        <div className={`mt-5 overflow-hidden ${cardCls}`}>
          {leadsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-7 w-7 animate-spin text-[#1F8296]" data-testid="leads-loading" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-[#7A8F96]" data-testid="leads-empty">
              <Inbox className="h-8 w-8" />
              <p className="text-sm">
                {query
                  ? "Nessun contatto corrisponde alla ricerca."
                  : filter === "aperti"
                    ? "Nessun contatto da lavorare. Ottimo lavoro!"
                    : "Nessun contatto in questa vista."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm" data-testid="leads-table">
                <thead>
                  <tr className="border-b border-[#D8E5E8] text-xs uppercase tracking-wider text-[#7A8F96]">
                    <th className="px-6 py-4 font-semibold">Nome</th>
                    <th className="px-6 py-4 font-semibold">Email</th>
                    <th className="px-6 py-4 font-semibold">Telefono</th>
                    <th className="px-6 py-4 font-semibold">Oggetto</th>
                    <th className="px-6 py-4 font-semibold">Stato</th>
                    <th className="px-6 py-4 font-semibold">Data</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead) => (
                    <tr
                      key={lead.id}
                      data-testid={`lead-row-${lead.id}`}
                      onClick={() => openLead(lead)}
                      className="cursor-pointer border-b border-[#E3EDEF] text-[#42565D] transition-colors last:border-0 hover:bg-[#F4F9FA]"
                    >
                      <td className="px-6 py-4 font-semibold text-[#1A2E33]">{lead.nome}</td>
                      <td className="px-6 py-4">{lead.email}</td>
                      <td className="px-6 py-4">{lead.telefono || "—"}</td>
                      <td className="max-w-[200px] truncate px-6 py-4">{lead.oggetto}</td>
                      <td className="px-6 py-4">
                        <span
                          data-testid={`lead-status-${lead.id}`}
                          className={`inline-flex rounded-full px-3 py-1 text-[11px] font-bold ${
                            lead.lavorato ? "bg-emerald-100 text-emerald-700" : "bg-[#E8F2F4] text-[#1F8296]"
                          }`}
                        >
                          {lead.lavorato ? "Lavorato" : "Da lavorare"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-[#7A8F96]">{formatDate(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-16 flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="font-plex text-xs uppercase tracking-[0.28em] text-[#1F8296]">Cosa dicono di noi</p>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-[#1A2E33] sm:text-3xl">
              Testimonianze dei partecipanti
            </h2>
            <p className="mt-2 max-w-xl text-sm text-[#5C7178]">
              Aggiungi qui i commenti e le referenze dei partecipanti: appariranno subito nella sezione
              "Cosa dicono di noi" della Home.
            </p>
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-12">
          <div className={`p-6 sm:p-8 lg:col-span-5 ${cardCls}`} data-testid="testimonial-form-card">
            <form onSubmit={addTesti} className="space-y-4">
              <div>
                <label htmlFor="testi-nome" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Nome del partecipante *</label>
                <Input
                  id="testi-nome"
                  data-testid="testimonial-name-input"
                  required
                  minLength={2}
                  value={tForm.nome}
                  onChange={(e) => setTForm((f) => ({ ...f, nome: e.target.value }))}
                  placeholder="Es. Maria G."
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="testi-corso" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Corso frequentato</label>
                <Input
                  id="testi-corso"
                  data-testid="testimonial-course-input"
                  value={tForm.corso}
                  onChange={(e) => setTForm((f) => ({ ...f, corso: e.target.value }))}
                  placeholder="Es. Corso Base di Informatica"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="testi-testo" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Commento *</label>
                <Textarea
                  id="testi-testo"
                  data-testid="testimonial-text-input"
                  required
                  minLength={5}
                  rows={4}
                  value={tForm.testo}
                  onChange={(e) => setTForm((f) => ({ ...f, testo: e.target.value }))}
                  placeholder="Scrivi qui il commento del partecipante..."
                  className="rounded-xl border-[#C9DBDF] bg-[#F4F9FA] text-[#1A2E33] placeholder:text-[#9AB0B7] focus-visible:ring-[#1F8296]/50"
                />
              </div>
              <div>
                <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#5C7178]">Valutazione</span>
                <div className="flex gap-1.5" data-testid="testimonial-rating-input">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`testimonial-star-${n}`}
                      onClick={() => setTForm((f) => ({ ...f, rating: n }))}
                      aria-label={`${n} stelle su 5`}
                      className="transition-transform hover:scale-110"
                    >
                      <Star className={`h-6 w-6 ${n <= tForm.rating ? "fill-[#2997AB] text-[#2997AB]" : "text-[#C9DBDF]"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="submit"
                data-testid="testimonial-submit-button"
                disabled={tSaving}
                className="inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#238496] disabled:opacity-60"
              >
                {tSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {tSaving ? "Pubblicazione..." : "Aggiungi testimonianza"}
              </button>
            </form>
          </div>

          <div className="space-y-4 lg:col-span-7" data-testid="testimonials-admin-list">
            {testis.length === 0 ? (
              <div className={`flex flex-col items-center gap-3 p-14 text-[#7A8F96] ${cardCls}`}>
                <Quote className="h-8 w-8" />
                <p className="text-sm">Nessuna testimonianza ancora. Aggiungi la prima dal modulo a fianco.</p>
              </div>
            ) : (
              testis.map((t) => (
                <div key={t.id} data-testid={`testimonial-item-${t.id}`} className={`flex items-start gap-4 p-6 ${cardCls}`}>
                  <Quote className="mt-1 h-5 w-5 shrink-0 text-[#2997AB]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-relaxed text-[#42565D]">"{t.testo}"</p>
                    <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <p className="text-sm font-semibold text-[#1A2E33]">{t.nome}</p>
                      {t.corso && <p className="text-xs text-[#7A8F96]">{t.corso}</p>}
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} className={`h-3.5 w-3.5 ${i <= (t.rating || 5) ? "fill-[#2997AB] text-[#2997AB]" : "text-[#C9DBDF]"}`} />
                        ))}
                      </div>
                      {t.esempio && (
                        <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-600">
                          Esempio
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid={`testimonial-delete-${t.id}`}
                    onClick={() => deleteTesti(t)}
                    aria-label={`Elimina testimonianza di ${t.nome}`}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-red-300 text-red-500 transition-colors hover:border-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="lead-detail-modal" className="max-w-lg border-[#D8E5E8] bg-white text-[#1A2E33]">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-[#1A2E33]">{selected.nome}</DialogTitle>
                <DialogDescription className="text-sm text-[#5C7178]">
                  Ricevuto il {formatDate(selected.created_at)} —{" "}
                  {selected.lavorato ? "Lavorato" : "Da lavorare"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2 text-sm">
                <p className="flex items-center gap-3 text-[#42565D]">
                  <Mail className="h-4 w-4 text-[#1F8296]" />
                  <a href={`mailto:${selected.email}`} className="text-[#1F8296] hover:text-[#238496]">{selected.email}</a>
                </p>
                <p className="flex items-center gap-3 text-[#42565D]">
                  <Phone className="h-4 w-4 text-[#1F8296]" />
                  {selected.telefono ? (
                    <a href={`tel:${selected.telefono}`} className="text-[#1F8296] hover:text-[#238496]">{selected.telefono}</a>
                  ) : "—"}
                </p>
                <div className="rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#7A8F96]">Oggetto</p>
                  <p className="mt-1 font-semibold text-[#1A2E33]">{selected.oggetto}</p>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-[#7A8F96]">Messaggio</p>
                  <p className="mt-1 whitespace-pre-wrap leading-relaxed text-[#42565D]">{selected.messaggio}</p>
                </div>

                <div className="rounded-2xl border border-[#D8E5E8] bg-[#F4F9FA] p-4">
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#7A8F96]">
                    <StickyNote className="h-3.5 w-3.5" /> Note interne (visibili solo a te)
                  </p>
                  <Textarea
                    data-testid="lead-note-input"
                    rows={3}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Es. chiamato il 26/09, interessato al corso base di ottobre..."
                    className="mt-2 rounded-xl border-[#C9DBDF] bg-white text-[#1A2E33] placeholder:text-[#9AB0B7] focus-visible:ring-[#1F8296]/50"
                  />
                  <button
                    type="button"
                    data-testid="lead-note-save"
                    onClick={saveNote}
                    disabled={noteSaving}
                    className="mt-2.5 inline-flex items-center gap-2 rounded-full border border-[#C9DBDF] px-5 py-2 text-xs font-bold text-[#1F8296] transition-colors hover:border-[#1F8296]/60 disabled:opacity-60"
                  >
                    {noteSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    Salva nota
                  </button>
                </div>

                <div className="flex flex-wrap gap-2.5">
                  <a
                    href={`mailto:${selected.email}?subject=${encodeURIComponent(`Re: ${selected.oggetto}`)}`}
                    data-testid="lead-reply-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2997AB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
                  >
                    <Reply className="h-4 w-4" /> Rispondi via email
                  </a>
                  <button
                    type="button"
                    data-testid="lead-toggle-lavorato"
                    onClick={() => toggleLavorato(selected)}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors ${
                      selected.lavorato
                        ? "border border-[#C9DBDF] text-[#5C7178] hover:border-[#1F8296]/50 hover:text-[#1F8296]"
                        : "bg-emerald-600 text-white hover:bg-emerald-500"
                    }`}
                  >
                    {selected.lavorato ? (
                      <><Undo2 className="h-4 w-4" /> Rimetti da lavorare</>
                    ) : (
                      <><CheckCircle2 className="h-4 w-4" /> Segna come lavorato</>
                    )}
                  </button>
                  <button
                    type="button"
                    data-testid="lead-delete-button"
                    onClick={deleteLead}
                    className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors ${
                      confirmDelete
                        ? "bg-red-600 text-white hover:bg-red-500"
                        : "border border-red-300 text-red-500 hover:border-red-500"
                    }`}
                  >
                    <Trash2 className="h-4 w-4" />
                    {confirmDelete ? "Conferma eliminazione" : "Elimina (GDPR)"}
                  </button>
                </div>
                {confirmDelete && (
                  <p className="text-xs leading-relaxed text-red-500" data-testid="lead-delete-warning">
                    Attenzione: l'eliminazione è definitiva. Usala quando l'interessato chiede la
                    cancellazione dei suoi dati (diritto all'oblio, art. 17 GDPR).
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </main>
  );
}
